/* ============================================================
   Real client — aligned to mia-data's webintegration.md (2026-08).
   This module ADAPTS the wire shapes to the app shapes that mock.js also
   implements, so no page ever branches on the provider.

   Wire (doc)                          App shape
   ─────────────────────────────────   ─────────────────────────
   POST /client/auth/x/login           login()/finishLogin() → tokens saved
   GET  /client/auth/me                me() → {name, handle:'', avatar:''}
   GET  /client/video-tasks/{id}       getTask() → {status:'processing'|'done'|'failed', result?, error?}
   GET  /share/video-tasks/{id}        getPublicResult(id) — the video share page
   GET  /share/songs/{id}              getPublicSong(id) — the song share page
   GET  /share/playlists/{id}          getPublicPlaylist(id) — the playlist share page (asked 2026-09-21, not live)
   GET  /client/video-tasks?limit…     listTasks(offset, limit) — mixed kinds
   GET  /client/invites                listInvites() → {limit, items:[{code, used, usedAt}]}
   POST /client/uploads/presign|get-url  uploadImage(file) / uploadAudio(file) / imageUrl(key)

   THE WORK IS THE AGENT'S NOW (2026-09-16): the roast playground is gone, and
   with it every write to /client/video-tasks from this file — songs, roasts and
   MVs are created through the harness (js/core/agent.js), which owns its own
   transport. What is left here is identity, invites, photos, and READING a task
   for the cards and the share pages. The endpoints this file no longer calls —
   POST /client/video-tasks, /{id}/retry, /{id}/publish, /client/video-tasks/quota,
   GET /client/x/users/{username} — are still the backend's to keep or retire.

   Known gaps vs the design (degraded on purpose, see README):
   result has no title/style/duration → title derived from target;
   video_url is a signed URL (~1 h) → app refreshes it on <video> error.
   ============================================================ */

import { request, ApiError } from './http.js';
import { ENV } from './env.js';
import { saveTokens, clearTokens, hasSession } from './token.js';
import { fromWire as plFromWire } from './playlist-shape.js';
import { audioType, AUDIO_MAX, AUDIO_ONLY_MSG } from '@/utils/audio-types.js';

/* ---------------- X OAuth: same-tab redirect + PKCE (doc §1) ----------------
   No popup anywhere: iOS refuses windows opened after an await, in-app
   browsers (X's own included) give the window no opener, and x.com now
   sends COOP which severs it on desktop too. The page leaves for the
   authorize URL and comes back through x-callback.html, which bounces to
   where the user was with code+state riding the RETURN URL; finishLogin()
   (called from boot) completes the exchange.

   Storage discipline (learned the hard way on iOS Safari, reproduced in
   the simulator): WEB STORAGE CANNOT CROSS THIS HOP. WebKit can hand a
   page a storage object disconnected from disk (bug 199928 class) — a
   localStorage write made before location.assign was reproducibly gone
   on return. So the pending record travels as a COOKIE (a different
   subsystem entirely — synchronous, cross-tab, survives COOP
   browsing-context-group swaps and returns that land in a new tab; the
   reason Auth0's SPA SDK defaults to cookie transaction storage), and
   the code itself travels in the RETURN URL. localStorage/sessionStorage
   copies remain as best-effort fallbacks only. */
const b64url = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
const randStr = (n) => b64url(crypto.getRandomValues(new Uint8Array(n))).slice(0, n);
const PEND_KEY = 'miya_x_pend',
  RESULT_KEY = 'miya_x_result';
const PEND_TTL = 15 * 60e3; /* an abandoned attempt must not greet a visit days later */
const readJSON = (store, k) => {
  try {
    return JSON.parse(store.getItem(k));
  } catch {
    return null;
  }
};
const setPendCookie = (obj) =>
  (document.cookie =
    PEND_KEY +
    '=' +
    encodeURIComponent(JSON.stringify(obj)) +
    '; max-age=900; path=/; samesite=lax' +
    (location.protocol === 'https:' ? '; secure' : ''));
const readPendCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)miya_x_pend=([^;]*)/);
  try {
    return m ? JSON.parse(decodeURIComponent(m[1])) : null;
  } catch {
    return null;
  }
};
const clearPend = () => {
  document.cookie = PEND_KEY + '=; max-age=0; path=/';
  try {
    localStorage.removeItem(PEND_KEY);
  } catch {}
};

/* invite gate probe (doc §1) — PUBLIC, rate-limited, costs nothing.
   {required:true, valid:bool} while the gate is up; {required:false,
   valid:null} once it retires. Probe with '' to read the switch. */
export const checkInvite = (code) =>
  request('/client/invites/check', { method: 'POST', auth: false, body: { code: String(code || '') } });

/* invites, holder side (docs/API-invites.md, proposed 2026-09-03 — the
   server implements to it). Wire: {limit, items:[{code, status, created_at,
   used_at}]}, unused first then used by used_at desc; the order IS the pile.
   The wire never carries who redeemed a code, and neither does this shape.
   `status` values we don't know (revoked/expired, if they ever come) read
   as used with no date. */
export async function listInvites() {
  const d = await request('/client/invites');
  const items = (d.items || []).map((x) => ({
    code: String(x.code || '')
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, ''),
    used: x.status !== 'unused',
    usedAt: x.status === 'used' && x.used_at ? new Date(x.used_at) : null,
  }));
  return { limit: Number(d.limit) || items.length, items };
}

/* resume: a JSON-serializable descriptor of what the user was doing —
   handed back untouched by finishLogin() after the round trip.
   inviteCode rides the pend cookie across the hop — new users need it
   in the token exchange while the server's gate is up. */
export async function login(resume, inviteCode) {
  if (!ENV.GOOGLE_CLIENT_ID) throw new ApiError('server', 'VITE_GOOGLE_CLIENT_ID is not configured');
  const verifier = randStr(64);
  const state = randStr(32);
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  const pend = {
    verifier,
    state,
    return: location.href,
    resume: resume ?? null,
    invite: String(inviteCode || ''),
    ts: Date.now(),
  };
  setPendCookie(pend);
  try {
    localStorage.setItem(PEND_KEY, JSON.stringify(pend));
  } catch {} /* best-effort copy */

  /* Google 授权页(契约 §1,2026-08-29 起主用)。与 X 完全同构:
     PKCE + state + 回调换 code;只改地址/scope/回调页。X 已暂停——
     官方应用被封;恢复时 authorize 地址换回 x.com、scope 换回
     tweet.read users.read、端点换回 /client/auth/x/login 即可,
     pend/resume/邀请码这套全部通用。 */
  const u = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  u.search = new URLSearchParams({
    response_type: 'code',
    client_id: ENV.GOOGLE_CLIENT_ID,
    redirect_uri: ENV.GOOGLE_REDIRECT_URI,
    scope: 'openid email profile' /* 身份三件套,无任何可写权限 */,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  location.assign(u);
  return new Promise(() => {}); // the page is leaving — park the caller
}

/* Call once at boot, before anything asks who the user is.
   → null                        no sign-in was in flight
   → { ok:true,  resume }        tokens saved
   → { ok:false, resume, error } 'cancelled' | 'unfinished' | message */
export async function finishLogin() {
  let pend = readPendCookie() || readJSON(localStorage, PEND_KEY);
  if (pend?.ts && Date.now() - pend.ts > PEND_TTL) {
    clearPend(); /* stale — discard silently */
    pend = null;
  }
  /* the URL is the primary carrier (survives every iOS storage loss);
     x-callback's sessionStorage copy is the fallback */
  let res = null;
  const q = new URLSearchParams(location.search);
  if (q.get('code') || q.get('error')) {
    res = { code: q.get('code'), state: q.get('state'), error: q.get('error') };
    const scrubbed = location.pathname + location.hash;
    history.replaceState(null, '', scrubbed); // scrub ?code from URL/history
  }
  if (!res) res = readJSON(sessionStorage, RESULT_KEY);
  if (!pend && !res) return null;
  clearPend();
  sessionStorage.removeItem(RESULT_KEY);
  const resume = pend?.resume ?? null;
  /* pend but no result: the user bailed out of the authorize page
     (X 时代还有 app universal link 吞跳转的已知 bug,Google 无此毛病,
     守卫照留) — offer a retry */
  if (!res) return { ok: false, resume, error: 'unfinished' };
  if (res.error) return { ok: false, resume, error: res.error === 'access_denied' ? 'cancelled' : String(res.error) };
  if (!pend || !res.code || res.state !== pend.state)
    return { ok: false, resume, error: 'sign-in got mixed up — try again' };
  try {
    const body = { code: res.code, code_verifier: pend.verifier, redirect_uri: ENV.GOOGLE_REDIRECT_URI };
    if (pend.invite) body.invite_code = pend.invite;
    const d = await request('/client/auth/google/login', { method: 'POST', auth: false, body });
    saveTokens(d);
    return { ok: true, resume, is_new: !!d.is_new }; /* 契约:注册=true,埋点 webLoginEnd 用 */
  } catch (e) {
    /* the invite gate answers in 403s (doc §1) — turn them into her lines */
    const t = (e.type || '') + ' ' + (e.message || '');
    const error = /invite-required/i.test(t)
      ? 'sign-ups are invite-only right now — add your code and try again'
      : /invite-invalid/i.test(t)
        ? 'that invite code is invalid, expired, or used up — try another'
        : e.message || 'sign-in failed';
    return { ok: false, resume, error };
  }
}

export const isAuthed = () => hasSession();

/* → app user; doc's /me has no handle/avatar (gap: initials only) */
export async function me() {
  const d = await request('/client/auth/me');
  return { user_id: d.id, name: d.display_name || 'you', handle: '', avatar: '' };
}

export async function logout() {
  const rt = localStorage.getItem('miya_refresh');
  if (rt)
    await request('/client/auth/logout', { method: 'POST', body: { refresh_token: rt }, auth: false }).catch(() => {});
  clearTokens();
}

/* ---------------- video tasks (doc §4 — unified, kind is a FIELD) ---------
   app mode ↔ wire kind: the app says 'general' (Real Life), the wire
   says 'anything'. Everything else is shape-identical across kinds. */
const modeOf = (kind) => (kind === 'diss' ? 'diss' : kind === 'anything' ? 'general' : 'roast');
const KIND_TITLE = { diss: 'X Post · ', general: 'Roast Anything · ', roast: 'X Profile · ' };
const targetLabel = (t, mode) =>
  mode === 'diss'
    ? 'post ' + (t ?? '')
    : mode === 'general'
      ? String(t ?? '')
      : '@' + String(t ?? '').replace(/^@/, '');
/* the pipeline names the case from story+photos; until result_text lands,
   guess a case name from the story — same heuristic as the mock */
const caseNameOf = (story = '') => {
  const m = String(story).match(/\bmy ([a-z]+)/i);
  const name = m ? 'your ' + m[1].toLowerCase() : String(story).trim().split(/\s+/).slice(0, 3).join(' ');
  return (name || 'your case').slice(0, 30);
};
const fallbackLabel = (raw, mode) => (mode === 'general' ? caseNameOf(raw.user_prompt) : targetLabel(raw.target, mode));
const mapResult = (raw) => {
  const mode = modeOf(raw.kind);
  return {
    id: raw.id,
    mode,
    /* result_text is the SONG TITLE (doc §4); constructed label is the
       fallback for old tasks that predate it */
    title: raw.result_text || KIND_TITLE[mode] + fallbackLabel(raw, mode),
    video_url: raw.video_url,
    lyrics_full: raw.lyrics_full || '' /* full lyrics; null on old tasks */,
    analysis: null /* no read text from the API — card stays hidden */,
    user_prompt: raw.user_prompt || '',
    /* anything's evidence photos — signed ~1h like video_url; refetch the
       task when they expire (history uses them for the case fan) */
    photo_urls: raw.photo_urls || [],
    /* publish-to-X fields (doc §4): null = never published */
    publish_status: raw.publish_status ?? null,
    tweet_url: raw.tweet_url || '',
    publish_error: raw.publish_error || '',
    /* the raw target — the diss handoff digs the tweet id out of it */
    target: raw.target ?? '',
    targets:
      mode === 'general'
        ? [{ handle: '', name: caseNameOf(raw.user_prompt), avatar: '' }]
        : [{ handle: targetLabel(raw.target, mode), name: String(raw.target ?? ''), avatar: '' }],
    created_at: raw.created_at,
  };
};
const mapStatus = (raw) =>
  raw.status === 'succeeded'
    ? { id: raw.id, status: 'done', result: mapResult(raw) }
    : raw.status === 'failed'
      ? { id: raw.id, status: 'failed', error: { message: raw.error || 'generation failed' } }
      : { id: raw.id, status: 'processing' }; // stage is always null per doc — app paces the UI

/* ---------------- image upload (doc §3 — browser-direct, anything only) ----
   Files never touch mia-data: presign per photo (parallel for several),
   then the browser PUTs straight to object storage. One ticket = one file;
   the returned key goes into photo_keys in the user's picking order. */
const IMG_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const IMG_MAX = 3 * 1024 * 1024; /* 3MB — the UI whispers first, this enforces */
export async function uploadImage(file) {
  if (!IMG_TYPES.includes(file.type)) throw new ApiError('invalid', 'photos must be PNG, JPEG or WebP');
  if (file.size > IMG_MAX) throw new ApiError('invalid', 'photos must be under 3MB');
  const t = await request('/client/uploads/presign', {
    method: 'POST',
    body: { kind: 'image', content_type: file.type, size_bytes: file.size },
  });
  /* plain fetch, NOT request(): the PUT goes to the storage host, no auth.
     Content-Type must match the presign exactly; t.headers ride verbatim. */
  const r = await fetch(t.upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type, ...(t.headers || {}) },
  });
  if (!r.ok) throw new ApiError('server', 'a photo failed to upload — try again', r.status);
  return t.key;
}

/* ---------------- audio upload (harness dev 2026-09-18, #292; mia-data §4.8 2026-09-22) ----
   The same door as photos: presign with kind 'audio', then a browser-direct PUT; the key lands under
   uploads/<uid>/audio/. The file is a conversation asset only — the room never calls
   POST /client/songs/upload (owner 2026-09-22: the upload feeds similar-music search, it is not a song of
   the user's). Types are mia-data's six (audio-types.js); the mime is decided by extension, since the
   browser's file.type is '' for m4a/flac on some systems, and presign body + PUT header must agree. */
export async function uploadAudio(file) {
  const type = audioType(file);
  if (!type) throw new ApiError('invalid', AUDIO_ONLY_MSG);
  if (file.size > AUDIO_MAX) throw new ApiError('invalid', 'audio files must be under 50MB');
  const t = await request('/client/uploads/presign', {
    method: 'POST',
    body: { kind: 'audio', content_type: type, size_bytes: file.size },
  });
  const r = await fetch(t.upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': type, ...(t.headers || {}) },
  });
  if (!r.ok) throw new ApiError('server', 'the file failed to upload — try again', r.status);
  return t.key;
}

/* a stored photo's read url (the desktop's music.imageUrl): POST /client/uploads/get-url {key} → {url} */
export async function imageUrl(key) {
  const r = await request('/client/uploads/get-url', { method: 'POST', body: { key } });
  return (r && r.url) || '';
}

export async function getTask(id) {
  const raw = await request(`/client/video-tasks/${id}`);
  return mapStatus(raw);
}

/* ---------------- public share (doc §4, unified) ---------------- */
export async function getPublicResult(id) {
  const raw = await request(`/share/video-tasks/${id}`, { auth: false });
  const mode = modeOf(raw.kind);
  if (raw.status === 'succeeded')
    return { status: 'done', mode, targets: mapResult(raw).targets, result: mapResult(raw) };
  if (raw.status === 'failed') return { status: 'failed', mode, targets: mapResult(raw).targets };
  return {
    status: 'pending',
    mode,
    targets:
      mode === 'general'
        ? [{ handle: '', name: caseNameOf(raw.user_prompt) }]
        : [{ handle: targetLabel(raw.target, mode), name: '' }],
  };
}

/* ---------------- history: ONE mixed list, server-sorted new→old ----------
   → { items: [...app shape], more: boolean } */
/* ---------------- public song (the desktop app's originals) ----------------
   TODO(doc): path and field names are the backend's to finalize — the
   contract we designed to (design branch, song-share/README.md):
     GET /share/songs/{id} → { id, title, audio_url, lrc, is_owner? }
   Public; the bearer rides along when there is a session so the server can
   say is_owner. Unknown id → 404 → ApiError not_found (the page's 404). */
export async function getPublicSong(id) {
  const path = `/share/songs/${encodeURIComponent(id)}`;
  let d;
  try {
    d = await request(path, { auth: hasSession() });
  } catch (e) {
    /* a dead session (refresh failed → tokens cleared) must not turn a public
       page into a 404 — ask again as a stranger */
    if (e.code !== 'unauthorized') throw e;
    d = await request(path, { auth: false });
  }
  return {
    id: String(d.id ?? id),
    title: d.title || '',
    audio_url: d.audio_url || d.audioUrl || '' /* 契约:无音频资产为 null → '' */,
    lrc: d.lrc || d.lyrics_lrc || '' /* 契约:未跑对齐为 null → 无歌词形态 */,
    is_owner: !!(d.is_owner ?? d.isOwner) /* 契约:永不 401,token 失效=false */,
    duration_ms: Number(d.duration_ms) || 0 /* 时长首屏种子;文件元数据落地后覆盖 */,
  };
}

/* ---------------- public playlist (the share page, /playlists/{id}) ----------------
   ASKED of mia-data 2026-09-21 (docs/API-playlists.md §share) — not live yet; the page is built ahead of it:
     GET /share/playlists/{id} → { id, name, description, cover_image, is_owner?,
                                   songs: [{ song_id, title, duration_ms, cover_image, audio_url|null, lyrics_lrc }] }
   Songs come RESOLVED: a stranger has no bearer for /client/songs/batch or /audio-urls. The bearer rides along when
   there is a session so the server can say is_owner; a dead session asks again as a stranger (the song page's rule).
   Unknown id / a list its owner never shared → 404 → ApiError not_found (the page's 404). */
export async function getPublicPlaylist(id) {
  const path = `/share/playlists/${encodeURIComponent(id)}`;
  let d;
  try {
    d = await request(path, { auth: hasSession() });
  } catch (e) {
    if (e.code !== 'unauthorized') throw e;
    d = await request(path, { auth: false });
  }
  return plFromWire(d, id);
}

export async function listTasks(offset = 0, limit = 20) {
  const raw = await request(`/client/video-tasks?limit=${limit}&offset=${offset}`);
  const items = (raw.items || []).map((x) => {
    const mode = modeOf(x.kind);
    return {
      id: x.id,
      mode,
      title: KIND_TITLE[mode] + fallbackLabel(x, mode),
      targets: [fallbackLabel(x, mode)],
      created_at: x.created_at,
      status: x.status,
    };
  });
  return { items, more: (raw.items || []).length === limit };
}

/* ============================================================
   歌单(契约 §4.5,2026-09-20):对象不带歌;内容分页;改动走
   专用端点(加/删批量、挪位单曲)。全部 🔒 Bearer,仅本人。
   前端消费层在 js/room/playlists.js(远端模式)——那边把
   name/description/cover_image 翻译成房间的 title/intent/cover。
   v1 无分享(owner 2026-09-20:library 的 share 已藏)。
   ============================================================ */
export const listPlaylists = () => request('/client/playlists');
export const createPlaylist = (name, description = '') =>
  request('/client/playlists', { method: 'POST', body: description ? { name, description } : { name } });
export const renamePlaylist = (id, patch) =>
  request(`/client/playlists/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
export const deletePlaylist = (id) => request(`/client/playlists/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const playlistSongs = (id, offset = 0, limit = 50) =>
  request(`/client/playlists/${encodeURIComponent(id)}/songs?limit=${limit}&offset=${offset}`);
export const addPlaylistSongs = (id, songIds) =>
  request(`/client/playlists/${encodeURIComponent(id)}/songs`, { method: 'POST', body: { song_ids: songIds } });
export const removePlaylistSongs = (id, songIds) =>
  request(`/client/playlists/${encodeURIComponent(id)}/songs`, { method: 'DELETE', body: { song_ids: songIds } });
export const movePlaylistSong = (id, songId, afterSongId) =>
  request(`/client/playlists/${encodeURIComponent(id)}/songs/${encodeURIComponent(songId)}/position`, {
    method: 'PUT',
    body: { after_song_id: afterSongId },
  });
/* 红心的服务端记忆:契约点名 DELETE /client/songs/{id}/like 是"现有取消点赞";
   加赞按对称 PUT 接(⚠️ 动词待 data 确认,若是 POST 只改这一行) */
export const likeSong = (id) => request(`/client/songs/${encodeURIComponent(id)}/like`, { method: 'PUT' });
export const unlikeSong = (id) => request(`/client/songs/${encodeURIComponent(id)}/like`, { method: 'DELETE' });
/* 歌单本身的架上排序(契约 2026-09-20):拖一次发一次;null = 自建区最前(虚拟两项恒置顶) */
export const movePlaylist = (id, afterPlaylistId) =>
  request(`/client/playlists/${encodeURIComponent(id)}/position`, {
    method: 'PUT',
    body: { after_playlist_id: afterPlaylistId },
  });
